import {
  DetailsList,
  MessageBarType,
  SelectionMode,
  Stack
} from "@fluentui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { uploadsColumns } from "./uploadsColumns";

import { Upload } from ".";

import { AlertTypes } from "@/common/types/AlertTypes";
import { useApi } from "@/core/libs/api/react-query/useApi";
import { useAlert } from "@/hooks/useAlert";
import { attachmentTypeToLabel } from "@/modules/Contracts/fields/fields";
import { getFileExtension, removeFileExtension } from "@/utils/FileExtensions";
import { formatDate } from "@/utils/FormatDate";

interface DocumentUploadTableProp {
  uploads: Array<Upload>;
}

function DocumentUploadTable({
  uploads
}: DocumentUploadTableProp): JSX.Element {
  const { execute } = useApi();
  const { createAlert } = useAlert();
  const accountsQueryClient = useQueryClient();

  const getFileDownloadUrl = useCallback(
    async (id: number) => {
      const response = await execute<string>({
        url: `contracts/uploads/${id}/download`,
        method: "GET",
        showAlert: false
      });

      if (response.error) {
        createAlert({
          level: MessageBarType.error,
          type: AlertTypes.API,
          message: "Erro ao baixar anexo"
        });

        throw new Error(response.error.message);
      }

      return response.data;
    },
    [createAlert, execute]
  );

  const formattedUploads = useMemo(() => {
    return uploads.map(upload => ({
      id: upload.id,
      name: removeFileExtension(upload.fileName),
      type: getFileExtension(upload.fileName),
      date: formatDate(upload.createdAt),
      attachmentType: attachmentTypeToLabel[upload.attachmentType],
      link: () => getFileDownloadUrl(upload.id)
    }));
  }, [uploads, getFileDownloadUrl]);

  const handleDelete = useCallback(
    async (id: number) => {
      const response = await execute({
        url: `contracts/uploads/${id}`,
        method: "DELETE",
        showAlert: true,
        messages: {
          success: "Anexo deletado com sucesso",
          error: "Erro ao deletar anexo"
        }
      });

      if (response.response.ok) {
        await accountsQueryClient.invalidateQueries({
          queryKey: ["contract-uploads"]
        });
      }
    },
    [accountsQueryClient, execute]
  );

  return (
    <Stack>
      <Stack.Item>
        <div>
          <DetailsList
            selectionMode={SelectionMode.none}
            items={formattedUploads}
            columns={uploadsColumns(handleDelete)}
            setKey="uploadsItems"
            styles={{
              headerWrapper: {
                selectors: {
                  "& .ms-DetailsHeader": {
                    paddingTop: 0
                  }
                }
              }
            }}
          />
        </div>
      </Stack.Item>
    </Stack>
  );
}

export default DocumentUploadTable;
